import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate } from 'react-router-dom';
import {
  CompanyInfoElements,
  CompanyInformationContainer,
  PriceContainer,
} from '../../../common/reusable/QuotationCard.styles';
import {
  LogoResultContainer,
  PriceResult,
  QuoteInformationResultContainer,
  QuoteInformationResultItem,
} from '../../../common/reusable/QuotationCardResult.styles';
import { RiskCardSelectedStyles } from '../../../common/components/redesignComponents/Cards/RiskCardSelected.styles';
import { FormInputNumber } from '../../../common/formInputs/FormInputNumber';
import { luceSubmitForm } from '../../../features/forms/formsActions';
import infoIcon from '../../../assets/redesign_icons/info-icon.svg';
import LuceInfoModal from '../../../common/components/redesignComponents/Modal/LuceInfoModal';
import LoadingAnimation from '../../../common/Loader/LoadingAnimation';

function Luce() {
  const [quotes, setQuotes] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [annualSavings, setAnnualSavings] = useState(null);
  const [showSavingsResult, setShowSavingsResult] = useState(false);

  const openModal = () => setShowModal(!showModal);
  const navigate = useNavigate();

  const {
    register: registerQuote,
    handleSubmit: handleQuoteSubmit,
    formState: { errors: quoteErrors, touchedFields: quoteTouched },
  } = useForm({
    mode: 'all',
    defaultValues: {
      consumo: 2700,
      potenza: 3,
    },
    resolver: yupResolver(
      yup.object({
        consumo: yup
          .number()
          .typeError('Consumo deve essere un numero')
          .required('Consumo è obbligatorio')
          .min(1, 'Consumo non può essere negativo'),
        potenza: yup
          .number()
          .typeError('Potenza deve essere un numero')
          .required('Potenza è obbligatoria')
          .min(3, 'Potenza minima è 3 kW')
          .max(16.5, 'Potenza massima è 16.5 kW'),
      }),
    ),
  });

  const {
    register: registerSavings,
    handleSubmit: handleSavingsSubmit,
    getValues: getSavingsValues,
    formState: { errors: savingsErrors, touchedFields: savingsTouched },
  } = useForm({
    mode: 'all',
    defaultValues: {
      costo_attuale: '',
    },
    resolver: yupResolver(
      yup.object({
        costo_attuale: yup
          .number()
          .typeError('Costo attuale deve essere un numero')
          .min(1, 'Il costo non può essere negativo o zero')
          .test(
            'max-decimals',
            'Massimo due cifre decimali',
            (value) => value === undefined || /^\d+(\.\d{1,2})?$/.test(value.toString()),
          )
          .required('Il costo attuale è obbligatorio'),
      }),
    ),
  });

  useEffect(() => {
    const fetchDefaultQuotes = async () => {
      setLoading(true);
      try {
        const response = await luceSubmitForm({ consumo: 2700, potenza: 3 });
        setQuotes(response.data);
      } catch {
        // eslint-disable-next-line no-console
        console.log('Error fetching default quotes');
      } finally {
        setLoading(false);
      }
    };
    fetchDefaultQuotes();
  }, []);

  const onSubmit = async (data) => {
    setLoading(true);
    setShowSavingsResult(false);
    try {
      const response = await luceSubmitForm({ consumo: data.consumo, potenza: data.potenza });
      setQuotes(response.data);
    } catch {
      // eslint-disable-next-line no-console
      console.log('Error submitting form');
    } finally {
      setLoading(false);
    }
  };

  const calculatePrice = () => {
    const costo_attuale = getSavingsValues('costo_attuale');
    const annualCurrentCost = costo_attuale * 12;
    const annualNewCost = quotes[0]?.fees?.total ? quotes[0].fees.total * 12 : 0;
    const savings = annualCurrentCost - annualNewCost;
    setAnnualSavings(savings);
    setShowSavingsResult(true);
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12 col-md-4 d-flex flex-column justify-content-start align-items-center" style={{ minHeight: '85vh' }}>
          <div className="rounded-4 p-4 shadow-sm w-100 mt-2" style={{ border: '2px solid #00a651' }}>
            <h2 className="text-center mt-3 mb-4 text-dark fw-semibold fs-5 w-100">
              Inserisci potenza e consumo
            </h2>
            <hr className="bg-dark w-100" style={{ height: '2px' }} />
            <form onSubmit={handleQuoteSubmit(onSubmit)} className="w-100">
              <FormInputNumber
                registration={registerQuote('consumo')}
                error={quoteErrors.consumo?.message}
                valid={quoteTouched.consumo && !quoteErrors.consumo}
                label="Consumo Mensile (kWh)"
                min={0}
              />
              <FormInputNumber
                registration={registerQuote('potenza')}
                step={0.1}
                min={3}
                max={16.5}
                error={quoteErrors.potenza?.message}
                valid={quoteTouched.potenza && !quoteErrors.potenza}
                label="Potenza (kW)"
              />
              <button type="submit" className="btn btn-success w-100 mt-3">Calcola Prezzo</button>
            </form>
          </div>

          <div className="rounded-4 p-4 shadow-sm w-100 mt-4" style={{ border: '2px solid #00a651' }}>
            <h2 className="text-center mt-3 mb-4 text-dark fw-semibold fs-5 w-100">
              Confronta con attuale fornitura
            </h2>
            <hr className="bg-dark w-100" style={{ height: '2px' }} />
            <form onSubmit={handleSavingsSubmit(() => calculatePrice())} className="w-100">
              <FormInputNumber
                registration={registerSavings('costo_attuale')}
                error={savingsErrors.costo_attuale?.message}
                valid={savingsTouched.costo_attuale && !savingsErrors.costo_attuale}
                label="Costo attuale (€)"
                min={0}
              />
              <button type="submit" className="btn btn-success w-100 mt-3">Risparmio Annuo</button>
            </form>
            {showSavingsResult && (
              <div className={`alert mt-3 ${annualSavings > 0 ? 'alert-success' : 'alert-danger'}`}>
                {annualSavings > 0
                  ? `Il Cliente Risparmia: ${annualSavings.toFixed(2)}€`
                  : ` Il Cliente non ha convenienza ${annualSavings.toFixed(2)}€`}
              </div>
            )}
          </div>
        </div>

        <div className="col-12 col-md-8">
          <div className="d-flex flex-column align-items-center gap-3 mt-2" style={{ maxHeight: '100vh', overflowY: 'auto', paddingRight: '0.5rem' }}>
            {loading && (
              <div className="mt-5">
                <LoadingAnimation />
                <p className="text-dark fw-semibold">Caricando preventivi...</p>
              </div>
            )}
            {!loading
              && quotes.length > 0
              && quotes.map((quote) => (
                <RiskCardSelectedStyles
                  key={quote.id}
                  className="shadow-sm position-relative"
                  style={{
                    border: '2px solid rgb(0, 165, 81)',
                    width: '100%',
                    maxWidth: '800px',
                    overflow: 'hidden',
                    wordWrap: 'break-word',
                  }}
                >
                  <button
                    className="btn btn-success position-absolute"
                    style={{ top: '10px', right: '20px', zIndex: 2 }}
                    onClick={() => navigate('/quote/luce/frame')}
                  >
                    Vai all'offerta
                  </button>

                  <div
                    className="d-flex flex-column align-items-start gap-3 p-3"
                    style={{ width: '100%' }}
                  >
                    <div
                      className="d-flex align-items-center gap-4 flex-wrap"
                      style={{ width: '100%' }}
                    >
                      <LogoResultContainer>
                        <img
                          src={quote?.provider_logo}
                          alt={quote?.provider_name}
                          style={{
                            maxWidth: '100px',
                            maxHeight: '50px',
                            width: '100%',
                            objectFit: 'contain',
                          }}
                        />
                      </LogoResultContainer>
                      <PriceContainer className="flex-grow-1">
                        <PriceResult>
                          <h5 className="mb-0">Prezzo</h5>
                          <h2 className="mb-0">
                            &euro;
                            {quote?.fees?.total?.toFixed?.(2) ?? 'N/A'}
                          </h2>
                        </PriceResult>
                      </PriceContainer>
                    </div>

                    <CompanyInformationContainer>
                      <CompanyInfoElements onClick={openModal}>
                        <img src={infoIcon} alt="" />
                        <p>Maggiori informazioni</p>
                      </CompanyInfoElements>
                    </CompanyInformationContainer>

                    <QuoteInformationResultContainer className="d-flex flex-wrap justify-content-between gap-2 w-100">
                      <QuoteInformationResultItem>
                        <h6>Compagnia:</h6>
                        <p>{quote?.provider_name ?? 'N/A'}</p>
                      </QuoteInformationResultItem>
                      <QuoteInformationResultItem>
                        <h6>Prodotto:</h6>
                        <p>{quote?.name ?? 'N/A'}</p>
                      </QuoteInformationResultItem>
                      <QuoteInformationResultItem>
                        <h6>Durata:</h6>
                        <p>
                          {quote?.dettaglio?.DURATA ?? 'N/A'}
                          {' '}
                          mesi
                        </p>
                      </QuoteInformationResultItem>
                    </QuoteInformationResultContainer>
                  </div>
                </RiskCardSelectedStyles>
              ))}

            {!loading && quotes.length === 0 && <p className="alert alert-info">Nessun preventivo ancora disponibile</p>}
            {showModal && <LuceInfoModal quotation={quotes[0]} setShowModal={setShowModal} />}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Luce;
